import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';

const About: React.FC = () => {
  return (
    <div>
       <div className="bg-navy text-white py-5 text-center">
           <Container>
               <h1 className="display-4 fw-bold">About 515 Soccer</h1>
               <p className="lead">More than just a club. A community.</p>
           </Container>
       </div>

       <Container className="py-5">
           {/* Origin Story */}
           <Row className="mb-5 justify-content-center">
               <Col lg={9}>
                   <h2 className="text-teal fw-bold mb-3">Our Origin</h2>
                   <p className="lead text-muted">Founded in 2023 on the East Side of Des Moines.</p>
                   <p>
                       515 Soccer Academy was born from observing the limitations of traditional youth soccer tryouts. 
                       We witnessed local clubs cutting the majority of applicants based on short assessments that often fail to capture a player's long-term potential.
                   </p>
                   <p>
                       We decided to create something different: a club focused on <strong>development over recruitment</strong>. 
                       We believe that talent can be built, and our mission is simple: <em>Make higher level soccer available to more Central Iowa kids.</em>
                   </p>
               </Col>
           </Row>

           {/* Core Values */}
           <h2 className="text-center fw-bold text-navy mb-5">Our Values</h2>
           <Row className="g-4 mb-5">
               {[
                   { title: "Courage", text: "Try new things, face adversity, and receive feedback with an open mind." },
                   { title: "Integrity", text: "Do the right thing, even if it means losing. Character counts." },
                   { title: "Creativity", text: "Ask questions, try new things, learn, and grow." },
                   { title: "Curiosity", text: "Find new and unique ways to solve problems on the field." },
                   { title: "Kinship", text: "Create a sense of belonging and care for each other." }
               ].map((value, idx) => (
                   <Col md={4} key={idx}>
                       <Card className="h-100 border-0 shadow-sm hover-shadow">
                           <Card.Body className="text-center">
                               <h3 className="h4 fw-bold text-teal">{value.title}</h3>
                               <Card.Text>{value.text}</Card.Text>
                           </Card.Body>
                       </Card>
                   </Col>
               ))}
           </Row>

           {/* Philosophy */}
           <div className="bg-light p-5 rounded shadow-sm mb-5">
               <Row className="align-items-center">
                   <Col md={8}>
                       <h2 className="fw-bold text-navy">The Growth Mindset</h2>
                       <p className="fs-5">
                           We believe that talents can be developed through hard work, good strategies, and input from others.
                           We avoid the "fixed mindset" trap that assumes talent is innate and unchangeable.
                       </p>
                       <p>
                           <strong>"Champions Do Extra."</strong> This isn't just a motto; it's a call to action. 
                           It means doing the extra work when no one is watching, supporting your teammates, and pushing yourself to be better than you were yesterday.
                       </p>
                   </Col>
                   <Col md={4} className="text-center">
                       <div className="d-inline-block p-4 rounded-circle bg-white shadow text-primary fw-bold display-6" style={{width: '200px', height: '200px', lineHeight: '150px'}}>
                           Growth
                       </div>
                   </Col>
               </Row>
           </div>

           {/* Proof of Process Image */}
           <Row className="mb-5 align-items-center">
                <Col md={6} className="order-md-2">
                    <img 
                        src="/assets/images/PXL_20251102_221207810.jpg" 
                        alt="515 Soccer Team with Trophy" 
                        className="img-fluid rounded shadow-lg"
                    />
                </Col>
                <Col md={6} className="order-md-1">
                    <h2 className="text-teal fw-bold mb-3">Development Leads to Trophies</h2>
                    <p className="lead">
                        This moment captures what 515 Soccer is all about. 
                    </p>
                    <p>
                        We don't chase trophies; we chase growth. But when you focus on the process, develop every player, 
                        and build a team culture based on kinship and hard work, the results follow.
                    </p>
                    <p className="fst-italic">
                        "We don't need tryouts to win. We need time, patience, and hard work."
                    </p>
                </Col>
           </Row>

           {/* Club Leadership */}
           <div className="mt-5">
               <h2 className="text-center fw-bold text-navy mb-4">Club Leadership</h2>
               <p className="text-center text-muted mb-5">
                   Our volunteer board guides the strategic direction of the club, ensuring we stay true to our mission.
               </p>
               
               <Row className="justify-content-center g-4">
                   <Col md={6} lg={4}>
                       <Card className="h-100 border-0 shadow-sm text-center py-3">
                           <Card.Body>
                                <h4 className="fw-bold text-navy">Bo Schatzberg</h4>
                                <p className="text-teal fw-bold mb-2">President & Executive Director</p>
                                <a href="mailto:bo@515soccer.org" className="text-decoration-none">bo@515soccer.org</a>
                           </Card.Body>
                       </Card>
                   </Col>
                   
                   <Col md={6} lg={4}>
                       <Card className="h-100 border-0 shadow-sm text-center py-3">
                           <Card.Body>
                                <h4 className="fw-bold text-navy">Andy Schryver</h4>
                                <p className="text-teal fw-bold mb-0">Secretary</p>
                           </Card.Body>
                       </Card>
                   </Col>

                   <Col md={6} lg={4}>
                       <Card className="h-100 border-0 shadow-sm text-center py-3">
                           <Card.Body>
                                <h4 className="fw-bold text-navy">Kevin Connell</h4>
                                <p className="text-teal fw-bold mb-0">Treasurer</p>
                           </Card.Body>
                       </Card>
                   </Col>

                   <Col md={6} lg={4}>
                       <Card className="h-100 border-0 shadow-sm text-center py-3">
                           <Card.Body>
                                <h4 className="fw-bold text-navy">Jacob Misel</h4>
                                <p className="text-teal fw-bold mb-0">Board Member</p>
                           </Card.Body>
                       </Card>
                   </Col>

                   <Col md={6} lg={4}>
                       <Card className="h-100 border-0 shadow-sm text-center py-3">
                           <Card.Body>
                                <h4 className="fw-bold text-navy">Lindsey Carley</h4>
                                <p className="text-teal fw-bold mb-0">Board Member</p>
                           </Card.Body>
                       </Card>
                   </Col>
               </Row>
           </div>
       </Container>
    </div>
  );
};

export default About;